import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpHandlerFn,
} from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';

// Define the interceptor as a function for newer Angular versions
export const AuthInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  // Get the auth service using inject
  const authService = inject(AuthService);

  // Skip authentication for certain URLs to prevent loops
  if (shouldSkipAuth(request.url)) {
    return next(request);
  }

  // Get the auth token
  const token = authService.getToken();

  // Clone the request and add the authorization header if token exists
  let authReq = request;
  if (token) {
    // Extract user ID from token
    const userId = extractUserIdFromToken(token);
    
    
    authReq = request.clone({
      headers: request.headers
        .set('Authorization', `Bearer ${token}`)
        .set('X-User-ID', userId || ''),
    });
  }

  // Process the modified request
  return next(authReq).pipe(
    catchError((error) => {
      // Handle 401 Unauthorized errors (expired token)
      if (error instanceof HttpErrorResponse && error.status === 401) {
        // Don't try to refresh tokens during the login process
        if (isLoginFlow(request.url) || !token) {
          return throwError(() => error);
        }
        
        // Try to refresh the token and retry the request
        return refreshTokenAndRetry(authService, authReq, next);
      }
      return throwError(() => error);
    })
  );
};

function shouldSkipAuth(url: string): boolean {
  // Skip authentication for Keycloak endpoints and other auth-related URLs
  const skipPatterns = [
    '/protocol/openid-connect/',
    '/realms/',
    '/auth/',
    'keycloak',
    '/assets/',
    '.html',
    '.css',
    '.js',
    '.png',
    '.jpg',
    '.svg',
    '.ico'
  ];
  
  return skipPatterns.some(pattern => url.includes(pattern));
}

function isLoginFlow(url: string): boolean {
  // Check if this is part of the login/authentication flow
  const loginPatterns = [
    '/protocol/openid-connect/token',
    '/protocol/openid-connect/userinfo',
    '/protocol/openid-connect/auth',
    '/realms/',
    'keycloak'
  ];
  
  return loginPatterns.some(pattern => url.includes(pattern));
}

function extractUserIdFromToken(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const decoded = JSON.parse(jsonPayload);
    
    // Use preferred_username as the user ID for Keycloak tokens
    return decoded.preferred_username || decoded.sub || decoded.email || null;
  } catch (e) {
    return null;
  }
}

function refreshTokenAndRetry(
  authService: AuthService,
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  console.log('Attempting token refresh for request:', request.url);
  
  return authService.refreshToken().pipe(
    switchMap(() => {
      // Get the new token
      const newToken = authService.getToken();

      if (!newToken) {
        console.error('No token available after refresh');
        authService.logout();
        return throwError(() => new Error('No token after refresh'));
      }

      // Clone the request with the new token
      const authReq = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${newToken}`),
      });

      console.log('Retrying request with new token');
      // Retry the request
      return next(authReq);
    }),
    catchError((refreshError) => {
      // If refresh failed, logout and redirect to login page
      console.error('Token refresh failed:', refreshError);
      
      // Only logout if we're not already in the auth flow
      if (!window.location.pathname.includes('/auth/')) {
        authService.logout();
      }
      
      return throwError(() => refreshError);
    })
  );
}